<script lang="ts">
  // Trigger + floating card. Opens on hover (with a short delay and a linger on
  // leave so you can move the pointer into it), on focus, and on click — the
  // last of which is the only way in on touch devices, where there is no hover.
  import type { Snippet } from "svelte";
  import { onDestroy } from "svelte";
  import { floating, hasHover, type Align } from "../lib/popover";

  let {
    trigger,
    content,
    align = "start",
    gap = 6,
    openDelay = 90,
    closeDelay = 180,
    disabled = false,
    role = "tooltip",
    label,
    cardClass = "",
  }: {
    trigger: Snippet;
    /** receives a `close` callback, for menu items that dismiss on pick */
    content: Snippet<[() => void]>;
    align?: Align;
    gap?: number;
    openDelay?: number;
    closeDelay?: number;
    disabled?: boolean;
    role?: "tooltip" | "menu";
    label?: string;
    cardClass?: string;
  } = $props();

  let anchorEl = $state<HTMLElement | null>(null);
  let cardEl = $state<HTMLElement | null>(null);
  let open = $state(false);

  let openTimer: ReturnType<typeof setTimeout> | undefined;
  let closeTimer: ReturnType<typeof setTimeout> | undefined;

  /** Hover is a capability, not a viewport size — a tablet with a trackpad has
   * it and a wide touch screen does not. Read once: it does not change. */
  const canHover = hasHover();

  function clearTimers() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    openTimer = undefined;
    closeTimer = undefined;
  }

  function show() {
    if (disabled) return;
    clearTimers();
    openTimer = setTimeout(() => {
      openTimer = undefined;
      open = true;
    }, openDelay);
  }

  function hide() {
    clearTimers();
    closeTimer = setTimeout(() => (open = false), closeDelay);
  }

  /** Dismiss deliberately (Escape, outside click, menu pick). The card is
   * portaled to <body>, so if focus is inside it we must hand focus back to the
   * trigger by hand — otherwise it lands on <body> and tabbing restarts at the
   * top of the page. */
  function close() {
    clearTimers();
    const inCard = cardEl?.contains(document.activeElement);
    open = false;
    if (inCard) {
      const back = anchorEl?.querySelector<HTMLElement>(
        'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      back?.focus({ preventScroll: true });
    }
  }

  function toggle() {
    if (disabled) return;
    // A queued open (from the focus that a tap also causes) counts as already
    // open, so the click that follows it does not immediately toggle it shut.
    const opening = openTimer !== undefined && !open;
    clearTimers();
    open = opening || !open;
  }

  function onWindowPointerDown(e: PointerEvent) {
    if (!open) return;
    const target = e.target as Node;
    if (anchorEl?.contains(target) || cardEl?.contains(target)) return;
    close();
  }

  /** mouseenter/mouseleave don't fire on a `display: contents` wrapper, so use
   * the bubbling pair and ignore moves between the trigger's own children. */
  function onLeave(e: MouseEvent, region: HTMLElement | null) {
    const to = e.relatedTarget as Node | null;
    if (to && region?.contains(to)) return;
    hide();
  }

  // a disabled trigger must not leave a stale card on screen
  $effect(() => {
    if (disabled && open) close();
  });

  onDestroy(clearTimers);
</script>

<svelte:window
  onpointerdown={onWindowPointerDown}
  onkeydown={(e) => e.key === "Escape" && open && close()}
/>

<!-- focusin/focusout (not focus/blur) because they bubble up from the real
     trigger inside this display:contents wrapper -->
<!-- svelte-ignore a11y_mouse_events_have_key_events -->
<div
  class="hc-anchor"
  bind:this={anchorEl}
  onmouseover={canHover ? show : undefined}
  onmouseout={canHover ? (e) => onLeave(e, anchorEl) : undefined}
  onfocusin={show}
  onfocusout={hide}
  onclick={toggle}
  role="presentation"
>
  {@render trigger()}
</div>

{#if open && !disabled}
  <!-- svelte-ignore a11y_mouse_events_have_key_events -->
  <div
    bind:this={cardEl}
    class="hc-card {cardClass}"
    class:menu={role === "menu"}
    {role}
    aria-label={label}
    onmouseover={canHover ? clearTimers : undefined}
    onmouseout={canHover ? (e) => onLeave(e, cardEl) : undefined}
    onfocusin={clearTimers}
    onfocusout={hide}
    use:floating={{ anchor: anchorEl, align, gap, onDismiss: close }}
  >
    {@render content(close)}
  </div>
{/if}

<style>
  .hc-anchor {
    display: contents;
  }

  /* :global because the card is portaled to <body>, outside this component's
     scoped-style subtree */
  :global(.hc-card) {
    z-index: var(--z-pop);
    /* size to the content, not to the anchor — this is what stopped names
       from being cut off */
    width: max-content;
    max-width: min(300px, calc(100vw - 24px));
    max-height: min(320px, 70vh);
    overflow: auto;
    overscroll-behavior: contain;
    padding: 7px;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 14px;
    box-shadow: var(--shadow);
    color: var(--ink);
    overflow-wrap: anywhere;
  }

  :global(.hc-card.menu) {
    padding: 6px;
  }
</style>
